import { useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { S } from '@/shared/constants/styles';
import { MONTHS } from '@/shared/utils/dateHelpers';
import { useTransactionFilters } from '../hooks/useTransactionFilters';
import { usePagination } from '@/shared/hooks/usePagination';
import { useIsMobile } from '@/shared/hooks/useMediaQuery';
import TransactionFilters from '../components/TransactionFilters';
import TransactionList from '../components/TransactionList';
import Pagination from '@/shared/components/Pagination';
import { useSettingsContext } from '@/app/providers/SettingsProvider';
import { useCategoriesContext } from '@/app/providers/CategoriesProvider';
import { usePeriod } from '@/app/providers/PeriodProvider';
import { useModals } from '@/app/providers/ModalsProvider';

export default function TransactionsPage() {
  const mobile = useIsMobile();
  const { dashboard, resolvedMemberNames } = useOutletContext();
  const { selMonth, selYear } = usePeriod();
  const { t, isDark, currency } = useSettingsContext();
  const { incomeList, expenseMap, getGroup } = useCategoriesContext();
  const { tx, setImportOpen } = useModals();

  const monthTxns = dashboard.monthTxns;
  const allGroups = ['Income', ...Object.keys(expenseMap)];
  const { openAddTx, openEditTx, deleteTx, deleteTxId, setDeleteTxId, openSplitTx, deleteAllSplits } = tx;

  const availableTags = useMemo(() => {
    const s = new Set();
    for (const txn of monthTxns) {
      for (const tag of (txn.tags || [])) s.add(tag);
    }
    return Array.from(s).sort();
  }, [monthTxns]);

  const {
    filterSearch, setFilterSearch,
    filterWho, setFilterWho,
    filterGroup, setFilterGroup,
    selectedTags, setSelectedTags,
    filteredTxns,
    clearFilters, hasActiveFilters,
  } = useTransactionFilters({ monthTxns, incomeList, getGroup });

  const pageSize = mobile ? 25 : 50;
  const mode = mobile ? 'loadMore' : 'paginated';
  const {
    paginatedItems, page, totalPages,
    hasNext, hasPrev, nextPage, prevPage,
    rangeStart, rangeEnd, totalItems,
  } = usePagination(filteredTxns, { pageSize, mode });

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
        <span style={{ fontSize: 13, color: "#94a3b8" }}>
          {totalItems > pageSize
            ? rangeStart + "-" + rangeEnd + " of " + totalItems + " transactions in " + MONTHS[selMonth] + " " + selYear
            : filteredTxns.length + " of " + monthTxns.length + " transactions in " + MONTHS[selMonth] + " " + selYear}
        </span>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => setImportOpen(true)} style={S.btn('#64748b')}>Import Statement</button>
          <button onClick={openAddTx} style={S.btn()}>+ Add Transaction</button>
        </div>
      </div>
      <TransactionFilters
        filterSearch={filterSearch} setFilterSearch={setFilterSearch}
        filterWho={filterWho} setFilterWho={setFilterWho}
        filterGroup={filterGroup} setFilterGroup={setFilterGroup}
        selectedTags={selectedTags} setSelectedTags={setSelectedTags}
        clearFilters={clearFilters} hasActiveFilters={hasActiveFilters}
        resolvedMemberNames={resolvedMemberNames} allGroups={allGroups}
        availableTags={availableTags} isDark={isDark}
      />
      <div style={{ ...S.card, padding: 0, overflow: "auto" }}>
        <TransactionList
          transactions={paginatedItems}
          onEdit={openEditTx}
          onDelete={deleteTx}
          onSplit={openSplitTx}
          onDeleteAllSplits={deleteAllSplits}
          deleteTxId={deleteTxId}
          setDeleteTxId={setDeleteTxId}
          incomeList={incomeList}
          getGroup={getGroup}
          currency={currency}
          theme={t}
          emptyMessage={monthTxns.length === 0 ? "No transactions this month" : "No transactions match your filters"}
        />
        <Pagination
          page={page} totalPages={totalPages}
          hasNext={hasNext} hasPrev={hasPrev}
          onNext={nextPage} onPrev={prevPage}
          rangeStart={rangeStart} rangeEnd={rangeEnd} totalItems={totalItems}
          theme={t} mode={mode}
        />
      </div>
    </div>
  );
}
